"use client";

/* @refresh reset */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useAuth } from "./AuthContext";
import {
  setCurrentOrgId as setModuleOrgId,
  setAuthOrgId,
} from "@/services/shared/infrastructure/tenant-state";

export interface Org {
  id: string;
  name: string;
  domain?: string;
  avatarUrl?: string;
  imageUrl?: string;
}

interface TenantContextType {
  // Current active tenant/organization ID (can override auth orgId)
  currentOrgId: string | null;

  // Available organizations for the current user
  availableOrgs: Org[];

  // Actions
  setTenant: (orgId: string) => void;
  setAvailableOrgs: (orgs: Org[]) => void;
  clearTenant: () => void;

  // Computed getters
  getCurrentOrg: () => Org | null;
  getEffectiveOrgId: () => string | null; // Falls back to auth orgId

  // Profile state getters
  getProfileName: () => string;
  getProfileInitials: () => string | null;
  getProfileImageUrl: () => string | null;
}

export const TenantContext = createContext<TenantContextType | undefined>(
  undefined,
);

/**
 * Hook to access tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

/**
 * Hook to get effective tenant ID (explicit override or auth orgId)
 */
export function useEffectiveTenantId(): string | null {
  const tenant = useTenant();
  const orgId = tenant.getEffectiveOrgId();

  // Defensive check: ensure we return a string or null, never an object
  if (orgId === null || orgId === undefined) {
    return null;
  }

  const orgIdStr = String(orgId);
  if (
    orgIdStr === "null" ||
    orgIdStr === "undefined" ||
    orgIdStr.includes("[object")
  ) {
    return null;
  }

  return orgIdStr;
}

/**
 * TenantProvider - Manages tenant/organization context
 */
function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [availableOrgs, setAvailableOrgs] = useState<Org[]>([]);

  // Get auth orgId
  const authOrgId = user?.orgIds?.[0] || null;

  const setTenant = useCallback((orgId: string) => {
    setCurrentOrgId(orgId);
  }, []);

  const setAvailableOrgsCallback = useCallback((orgs: Org[]) => {
    setAvailableOrgs(orgs);
  }, []);

  const clearTenant = useCallback(() => {
    setCurrentOrgId(null);
  }, []);

  // Sync currentOrgId to module-level state whenever it changes
  useEffect(() => {
    setModuleOrgId(currentOrgId);
  }, [currentOrgId]);

  // Sync authOrgId to module-level state
  useEffect(() => {
    setAuthOrgId(authOrgId);
  }, [authOrgId, user, currentOrgId]);

  const getCurrentOrg = useCallback((): Org | null => {
    if (!currentOrgId) return null;
    return availableOrgs.find((org) => org.id === currentOrgId) || null;
  }, [currentOrgId, availableOrgs]);

  const getEffectiveOrgId = useCallback((): string | null => {
    const effectiveId = currentOrgId || authOrgId || null;

    return effectiveId;
  }, [currentOrgId, authOrgId, user]);

  const getProfileName = useCallback((): string => {
    const currentOrg = getCurrentOrg();
    if (currentOrg?.name) return currentOrg.name;
    return "Virial AI";
  }, [getCurrentOrg]);

  const getProfileInitials = useCallback((): string | null => {
    const currentOrg = getCurrentOrg();
    if (!currentOrg?.name) return null;

    const name = currentOrg.name.trim();
    // Don't show initials for IDs or default fallbacks
    if (name.startsWith("ID_") || name === "Virial AI" || name.length === 0) {
      return null;
    }

    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return initials || null;
  }, [getCurrentOrg]);

  const getProfileImageUrl = useCallback((): string | null => {
    const currentOrg = getCurrentOrg();
    return currentOrg?.avatarUrl || currentOrg?.imageUrl || null;
  }, [getCurrentOrg]);

  const value = useMemo(
    () => ({
      currentOrgId,
      availableOrgs,
      setTenant,
      setAvailableOrgs: setAvailableOrgsCallback,
      clearTenant,
      getCurrentOrg,
      getEffectiveOrgId,
      getProfileName,
      getProfileInitials,
      getProfileImageUrl,
    }),
    [
      currentOrgId,
      availableOrgs,
      setTenant,
      setAvailableOrgsCallback,
      clearTenant,
      getCurrentOrg,
      getEffectiveOrgId,
      getProfileName,
      getProfileInitials,
      getProfileImageUrl,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

// Add display name for React Fast Refresh
TenantProvider.displayName = "TenantProvider";

// Export component separately to help Fast Refresh identify it
export { TenantProvider };
